// AuthContext — единственный источник истины о текущем юзере на фронте.
//
// status: 'loading' | 'guest' | 'authed'
//   loading — первый /me ещё не вернулся
//   guest   — не авторизован (или сессия истекла)
//   authed  — есть валидная сессия, user заполнен
//
// View-as (TZ §5.11):
//   user      — эффективный юзер (тот, чей UI рендерим)
//   realUser  — реальный владелец сессии (Создатель), null в обычном режиме
//   actingAs  — boolean-удобство = realUser !== null
//   enterActingAs(userId) / exitActingAs() — переключение режима
//
// Любая 401-ошибка из API глобально переводит контекст в guest (через
// CustomEvent 'auth-expired', диспатчится в api/client.js).

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    // state: { status, user, realUser, tenant }
    //   realUser !== null ⇒ View-as активен
    //   tenant — публичные поля tenant'а эффективного юзера {id, name} или null
    //     (для creator всегда null — он не привязан к тенанту).
    const [state, setState] = useState({
        status: 'loading',
        user: null,
        realUser: null,
        tenant: null,
    });

    const refresh = useCallback(async (signal) => {
        try {
            const { user, realUser, tenant } = await authApi.me(signal);
            setState({
                status: 'authed',
                user,
                realUser: realUser || null,
                tenant: tenant || null,
            });
            return user;
        } catch (err) {
            if (err?.name === 'AbortError') return null;
            if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
                setState({ status: 'guest', user: null, realUser: null, tenant: null });
                return null;
            }
            console.error('[auth] /me failed:', err);
            setState({ status: 'guest', user: null, realUser: null, tenant: null });
            return null;
        }
    }, []);

    useEffect(() => {
        const ctrl = new AbortController();
        refresh(ctrl.signal);
        return () => ctrl.abort();
    }, [refresh]);

    // Глобальный listener — при 401 от любого API-запроса синхронизируем
    // контекст в guest. ProtectedRoute сам отредиректит на /login.
    useEffect(() => {
        const onExpired = () => {
            setState((prev) =>
                prev.status === 'authed'
                    ? { status: 'guest', user: null, realUser: null, tenant: null }
                    : prev,
            );
        };
        window.addEventListener('auth-expired', onExpired);
        return () => window.removeEventListener('auth-expired', onExpired);
    }, []);

    const login = useCallback(async (username, password) => {
        const { user, realUser, tenant } = await authApi.login(username, password);
        setState({
            status: 'authed',
            user,
            realUser: realUser || null,
            tenant: tenant || null,
        });
        return user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch (err) {
            console.warn('[auth] logout server error (ignored):', err?.message);
        } finally {
            setState({ status: 'guest', user: null, realUser: null, tenant: null });
            // На всякий случай — сбрасываем кэш React Query (могут быть приватные данные)
            queryClient.clear();
        }
    }, [queryClient]);

    /** Обновить локально часть полей юзера без перезапроса /me. */
    const patchUser = useCallback((patch) => {
        setState((prev) =>
            prev.status === 'authed'
                ? { ...prev, user: { ...prev.user, ...patch } }
                : prev,
        );
    }, []);

    /** Принудительно перевести в гостя (например, при глобальном 401). */
    const setGuest = useCallback(() => {
        setState({ status: 'guest', user: null, realUser: null, tenant: null });
    }, []);

    /**
     * View-as: «провалиться» в учётку другого юзера. Доступно только Создателю.
     * После успеха кэш React Query очищается, чтобы все queries перезагрузились
     * с новым скоупом (иначе на странице остался бы список Tenant'ов от Creator'а).
     */
    const enterActingAs = useCallback(
        async (userId) => {
            const { user, realUser, tenant } = await authApi.enterActingAs(userId);
            setState({
                status: 'authed',
                user,
                realUser: realUser || null,
                tenant: tenant || null,
            });
            queryClient.clear();
            return user;
        },
        [queryClient],
    );

    /** Выйти из View-as обратно в реального юзера (Создателя). */
    const exitActingAs = useCallback(async () => {
        const { user, realUser, tenant } = await authApi.exitActingAs();
        setState({
            status: 'authed',
            user,
            realUser: realUser || null,
            tenant: tenant || null,
        });
        queryClient.clear();
        return user;
    }, [queryClient]);

    return (
        <AuthContext.Provider
            value={{
                status: state.status,
                user: state.user,
                realUser: state.realUser,
                tenant: state.tenant,
                actingAs: !!state.realUser,
                refresh,
                login,
                logout,
                patchUser,
                setGuest,
                enterActingAs,
                exitActingAs,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
